import React, { useEffect, useRef, useState } from 'react';

import { ACCENT, BORDURE, ENCRE } from './CarteAuth';

/* ═══════════════════════════════════════════════════════════════════════════
   CHAMP DE CODE À SIX CASES

   Une case par chiffre, avancement automatique, vérification déclenchée dès
   que la sixième est remplie. Aucun bouton de validation : il n'apporterait
   rien — le code est complet ou il ne l'est pas, et le savoir ne demande
   aucune décision de l'utilisateur.

   Quatre comportements que l'on n'obtient pas gratuitement :

   - Le collage. Un code reçu par e-mail se copie d'un bloc. Sans traitement
     du collage, les six chiffres atterrissent dans la première case et le
     reste est perdu.
   - Le retour arrière sur une case vide remonte à la précédente. Sinon,
     corriger une faute oblige à cliquer case par case.
   - `inputMode="numeric"` fait apparaître le pavé numérique sur mobile, où
     ce formulaire sera le plus souvent rempli.
   - `autoComplete="one-time-code"` laisse iOS et Android proposer le code
     lu dans le SMS ou l'e-mail, sans que l'utilisateur ait à le recopier.
   ═══════════════════════════════════════════════════════════════════════════ */

const LONGUEUR = 6;

export default function ChampCode({
  valeur = '',
  onChange,
  onComplet,
  desactive = false,
  erreur = false,
  autoFocus = true,
}) {
  const references = useRef([]);
  const [interne, setInterne] = useState(valeur.split(''));
  const [active, setActive] = useState(-1);
  const miroir = useRef(valeur.split(''));

  useEffect(() => {
    const chiffres = valeur.padEnd(LONGUEUR).slice(0, LONGUEUR)
      .split('').map((c) => c.trim());
    miroir.current = chiffres;
    setInterne(chiffres);
  }, [valeur]);

  useEffect(() => {
    if (autoFocus && references.current[0]) references.current[0].focus();
  }, [autoFocus]);

  const propager = (chiffres) => {
    const texte = chiffres.join('');
    onChange?.(texte);
    // La vérification part sur le dernier chiffre saisi, pas sur un clic.
    if (texte.length === LONGUEUR && !chiffres.includes('')) onComplet?.(texte);
  };

  /* Une référence double l'état, et ce n'est pas une redondance.

     Chaque frappe déplace le curseur sur la case suivante ; si l'on saisit
     plus vite que React ne redessine, la frappe suivante lit un état figé par
     la fermeture et écrase les chiffres déjà posés. Mesuré : sur une saisie
     rapide des six chiffres, un seul survivait.

     La forme fonctionnelle `setInterne(prec => …)` résout ce point, mais y
     placer l'appel au parent le ferait s'exécuter pendant le rendu — React le
     signale, et la mise à jour du parent serait perdue. La référence donne la
     valeur courante hors du cycle de rendu : on calcule, on enregistre, puis
     on prévient le parent. */
  const appliquer = (transformation) => {
    const chiffres = transformation([...miroir.current]);
    miroir.current = chiffres;
    setInterne(chiffres);
    propager(chiffres);
  };

  const ecrire = (index, brut) => {
    const chiffre = (brut || '').replace(/\D/g, '').slice(-1);
    appliquer((chiffres) => { chiffres[index] = chiffre; return chiffres; });
    if (chiffre && index < LONGUEUR - 1) references.current[index + 1]?.focus();
  };

  const auClavier = (index, evenement) => {
    if (evenement.key === 'Backspace') {
      if (interne[index]) return;            // la case se vide d'elle-même
      evenement.preventDefault();
      appliquer((chiffres) => { chiffres[index - 1] = ''; return chiffres; });
      references.current[index - 1]?.focus();
    } else if (evenement.key === 'ArrowLeft') {
      references.current[index - 1]?.focus();
    } else if (evenement.key === 'ArrowRight') {
      references.current[index + 1]?.focus();
    }
  };

  const auCollage = (evenement) => {
    const colle = (evenement.clipboardData.getData('text') || '').replace(/\D/g, '');
    if (!colle) return;
    evenement.preventDefault();
    const saisis = colle.slice(0, LONGUEUR).split('');
    while (saisis.length < LONGUEUR) saisis.push('');
    appliquer(() => saisis);
    references.current[Math.min(colle.length, LONGUEUR - 1)]?.focus();
  };

  return (
    <div className="flex justify-center gap-2 sm:gap-3" role="group"
         aria-label="Code de vérification à six chiffres">
      {Array.from({ length: LONGUEUR }).map((_, index) => (
        <input
          key={index}
          ref={(element) => { references.current[index] = element; }}
          type="text"
          inputMode="numeric"
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          maxLength={1}
          disabled={desactive}
          value={interne[index] || ''}
          onChange={(e) => ecrire(index, e.target.value)}
          onKeyDown={(e) => auClavier(index, e)}
          onPaste={auCollage}
          onFocus={(e) => { setActive(index); e.target.select(); }}
          aria-label={`Chiffre ${index + 1}`}
          onBlur={() => setActive(-1)}
          className="w-[46px] h-[58px] sm:w-[52px] sm:h-[64px] rounded-pastille text-center
                     text-[22px] font-semibold tabular-nums bg-white outline-none
                     transition-colors disabled:opacity-50"
          style={{
            color: ENCRE,
            // Couleurs en style plutôt qu'en classes : une classe Tailwind
            // construite à l'exécution n'est jamais générée, le compilateur
            // ne lisant que les chaînes littérales du source.
            border: `1.5px solid ${
              erreur ? '#F04438' : active === index ? ACCENT : BORDURE}`,
            boxShadow: active === index ? `0 0 0 3px ${ACCENT}22` : 'none',
          }}
        />
      ))}
    </div>
  );
}
